import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { StatusAlert } from "@/components/shared/StatusAlert";
import { TextLink } from "@/components/ui/text-link";
import { useAuth } from "@/hooks/useAuth";
import { DateFormatVariant, formatDate, resolveDateLocale } from "@/lib/dateFormat";
import { cn } from "@/lib/utils";

function canAccessOrgBilling(user: ReturnType<typeof useAuth>["user"]): boolean {
  return Boolean(user?.organization && user.isOrgAdmin && !user.organization.billingExempt);
}

export function BillingGraceBanner(): ReactElement | null {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const subscription = user?.subscription;
  const inGrace = subscription?.inBillingGrace === true;
  const graceEndsAt = subscription?.billingGraceEndsAt ?? null;

  if (!inGrace || !graceEndsAt) return null;

  const dateLocale = resolveDateLocale(i18n.language);
  const dateLabel = formatDate(graceEndsAt, dateLocale, DateFormatVariant.Date);
  const showBillingLink = canAccessOrgBilling(user);

  return (
    <StatusAlert
      variant="warning"
      className={cn(
        "relative z-50 flex items-center justify-center gap-x-3 rounded-none border-x-0 border-t-0 px-4 py-2.5 text-center sm:px-8",
        "[&>svg]:translate-y-0 [&_[data-slot=alert-description]]:col-start-auto",
      )}
    >
      <span>
        {t(showBillingLink ? "billing.graceBanner" : "billing.graceBannerMember", {
          date: dateLabel,
        })}
        {showBillingLink ? (
          <>
            {" "}
            <TextLink to="/settings/billing" className="font-medium underline underline-offset-2">
              {t("accessLocked.goToBilling")}
            </TextLink>
          </>
        ) : null}
      </span>
    </StatusAlert>
  );
}
